import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SortableHeader,
  TableSortResetBar,
  toggleSort,
  type SortState,
} from "@/components/dataTable";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { currentYear, shiftYear, yearDateRange } from "@/lib/auswertungen";
import {
  comparePipelinePersonRows,
  comparePipelineProjectRows,
  fetchHoursPipelineReport,
  formatPipelineHours,
  formatProjectLabel,
  HOURS_PIPELINE_STAGE_DESCRIPTIONS,
  HOURS_PIPELINE_STAGE_LABELS,
  HOURS_PIPELINE_STAGES,
  sortPipelineRows,
  sumPipelineStages,
  type HoursPipelinePersonRow,
  type HoursPipelineProjectRow,
  type HoursPipelineReport as HoursPipelineReportData,
  type HoursPipelineStage,
  type HoursPipelineStageMap,
} from "@/lib/hoursPipeline";
import { useRealtime } from "@/lib/useRealtime";
import "./auswertungen.css";

type DetailView = "project" | "person";

const DEFAULT_SORT: SortState = { key: "total", direction: "desc" };

function stageCellClass(stage: HoursPipelineStage, hours: number): string {
  if (hours <= 0) return "text-label-tertiary";
  return clsx("tabular-nums font-medium", `eh-hours-pipeline-cell--${stage}`);
}

function PipelineStageBar({
  summary,
  totalHours,
}: {
  summary: HoursPipelineStageMap;
  totalHours: number;
}) {
  if (totalHours <= 0) {
    return (
      <p className="text-sm text-label-secondary">Keine Stunden im gewählten Zeitraum.</p>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className="eh-hours-pipeline-bar flex h-8 w-full overflow-hidden rounded-lg ring-1 ring-separator/40"
        role="img"
        aria-label="Stundenverteilung nach Pipeline-Status"
      >
        {HOURS_PIPELINE_STAGES.map((stage) => {
          const hours = summary[stage] ?? 0;
          if (hours <= 0) return null;
          const width = (hours / totalHours) * 100;
          return (
            <div
              key={stage}
              className={clsx("eh-hours-pipeline-bar__segment", `eh-hours-pipeline-bar__segment--${stage}`)}
              style={{ width: `${width}%` }}
              title={`${HOURS_PIPELINE_STAGE_LABELS[stage]}: ${formatPipelineHours(hours)}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {HOURS_PIPELINE_STAGES.map((stage) => (
          <div key={stage} className="flex items-center gap-2 text-xs text-label-secondary">
            <span
              className={clsx(
                "inline-block h-2.5 w-2.5 rounded-sm",
                `eh-hours-pipeline-legend--${stage}`,
              )}
              aria-hidden
            />
            <span>{HOURS_PIPELINE_STAGE_LABELS[stage]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildStageColumns<T extends { stages: HoursPipelineStageMap }>(
  getRowTotal: (row: T) => number,
  sort: SortState,
  onSort: (key: string) => void,
): Column<T>[] {
  const stageColumns = HOURS_PIPELINE_STAGES.map<Column<T>>((stage) => ({
    key: stage,
    header: (
      <SortableHeader
        label={HOURS_PIPELINE_STAGE_LABELS[stage]}
        columnKey={stage}
        sort={sort}
        onSort={onSort}
      />
    ),
    render: (row) => {
      const hours = row.stages[stage] ?? 0;
      return <span className={stageCellClass(stage, hours)}>{formatPipelineHours(hours)}</span>;
    },
  }));

  return [
    ...stageColumns,
    {
      key: "total",
      header: (
        <SortableHeader label="Summe" columnKey="total" sort={sort} onSort={onSort} />
      ),
      render: (row) => (
        <span className="tabular-nums font-semibold text-label">
          {formatPipelineHours(getRowTotal(row))}
        </span>
      ),
    },
  ];
}

export function HoursPipelineReport() {
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState("");
  const [hideFullLicense, setHideFullLicense] = useState(true);
  const [detailView, setDetailView] = useState<DetailView>("project");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [report, setReport] = useState<HoursPipelineReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = useMemo(() => yearDateRange(year), [year]);

  const load = useCallback(
    async (opts?: { background?: boolean }) => {
      if (opts?.background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const data = await fetchHoursPipelineReport(from, to, { hideFullLicense });
        setReport(data);
        setError(null);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Stunden-Pipeline konnte nicht geladen werden.",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [from, to, hideFullLicense],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtime({
    scopes: ["domain:timesheets"],
    domains: ["timesheets"],
    events: ["timesheet_status_changed", "timesheet_hero_usage_updated", "timesheet_updated"],
    onChange: () => void load({ background: true }),
  });

  useEffect(() => {
    setSort(DEFAULT_SORT);
  }, [detailView, year]);

  const handleSort = useCallback((key: string) => {
    setSort((current) => toggleSort(current, key, "asc"));
  }, []);

  const sortIsCustom =
    sort.key !== DEFAULT_SORT.key || sort.direction !== DEFAULT_SORT.direction;

  const summary = report?.summary;
  const totalHours = summary?.total_hours ?? 0;
  const notInvoicedHours = summary?.not_invoiced_hours ?? 0;

  const filteredProjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = [...(report?.by_project ?? [])];
    if (term) {
      rows = rows.filter((row) => {
        const haystack = [formatProjectLabel(row), row.kunde]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");
        return haystack.includes(term);
      });
    }
    return sortPipelineRows(rows, sort.key, sort.direction, comparePipelineProjectRows);
  }, [report?.by_project, search, sort]);

  const filteredPersons = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = [...(report?.by_person ?? [])];
    if (term) {
      rows = rows.filter((row) =>
        String(row.person_name || "")
          .toLowerCase()
          .includes(term),
      );
    }
    return sortPipelineRows(rows, sort.key, sort.direction, comparePipelinePersonRows);
  }, [report?.by_person, search, sort]);

  const projectColumns = useMemo<Column<HoursPipelineProjectRow>[]>(
    () => [
      {
        key: "project",
        header: (
          <SortableHeader label="Projekt" columnKey="project" sort={sort} onSort={handleSort} />
        ),
        render: (row) => (
          <div>
            <ProjectOverviewLink auftragId={row.auftrag_id} label={formatProjectLabel(row)} />
            {row.kunde ? (
              <div className="text-xs text-label-secondary">{row.kunde}</div>
            ) : null}
          </div>
        ),
      },
      ...buildStageColumns((row) => sumPipelineStages(row.stages), sort, handleSort),
    ],
    [handleSort, sort],
  );

  const personColumns = useMemo<Column<HoursPipelinePersonRow>[]>(
    () => [
      {
        key: "person",
        header: (
          <SortableHeader label="Person" columnKey="person" sort={sort} onSort={handleSort} />
        ),
        render: (row) => row.person_name,
      },
      ...buildStageColumns((row) => sumPipelineStages(row.stages), sort, handleSort),
    ],
    [handleSort, sort],
  );

  const isCurrentYear = year === currentYear();

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Stunden-Pipeline konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Vorheriges Jahr"
              onClick={() => setYear((current) => shiftYear(current, -1))}
            >
              ‹
            </button>
            <div className="min-w-[5rem] text-center text-base font-semibold text-label">{year}</div>
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Nächstes Jahr"
              onClick={() => setYear((current) => shiftYear(current, 1))}
            >
              ›
            </button>
            {!isCurrentYear ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8"
                onClick={() => setYear(currentYear())}
              >
                Aktuelles Jahr
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {refreshing ? (
              <span className="flex items-center gap-1.5 text-xs text-label-secondary">
                <Spinner className="h-3.5 w-3.5" />
                wird aktualisiert …
              </span>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
              Neu laden
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <TextInput
            label="Suche"
            placeholder={detailView === "project" ? "Projekt oder Kunde …" : "Person …"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-col justify-end gap-2">
            <span className="text-xs font-medium text-label-secondary">Detailansicht</span>
            <div className="inline-flex rounded-lg border border-separator p-0.5">
              <button
                type="button"
                className={clsx(
                  "rounded-md px-3 py-1.5 text-sm transition",
                  detailView === "project"
                    ? "bg-accent text-white"
                    : "text-label-secondary hover:text-label",
                )}
                onClick={() => setDetailView("project")}
              >
                Nach Projekt
              </button>
              <button
                type="button"
                className={clsx(
                  "rounded-md px-3 py-1.5 text-sm transition",
                  detailView === "person"
                    ? "bg-accent text-white"
                    : "text-label-secondary hover:text-label",
                )}
                onClick={() => setDetailView("person")}
              >
                Nach Person
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <Checkbox
            label="Hero Lizenz Full ausblenden"
            checked={hideFullLicense}
            onChange={(e) => setHideFullLicense(e.target.checked)}
          />
          <p className="text-xs text-label-secondary">
            Nachvollziehbare Aufschlüsselung aller Stunden von der Zeiterfassung bis zur Bezahlung
            (Zeitraum: {year}, ohne Festpreis-Stunden).
          </p>
        </div>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Stunden-Pipeline wird geladen …</span>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 lg:col-span-1">
              <p className="text-xs text-label-secondary">Noch nicht abgerechnet</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatPipelineHours(notInvoicedHours)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 lg:col-span-1">
              <p className="text-xs text-label-secondary">Gesamt ({year})</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatPipelineHours(totalHours)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 lg:col-span-2">
              <p className="mb-2 text-xs text-label-secondary">Verteilung nach Status</p>
              {summary ? (
                <PipelineStageBar summary={summary} totalHours={totalHours} />
              ) : null}
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {HOURS_PIPELINE_STAGES.map((stage) => (
              <div
                key={stage}
                className={clsx(
                  "rounded-ios-lg px-4 py-3 shadow-ios-md ring-1 ring-separator/35",
                  "eh-hours-pipeline-card",
                  `eh-hours-pipeline-card--${stage}`,
                )}
              >
                <p className="text-xs font-medium text-label-secondary">
                  {HOURS_PIPELINE_STAGE_LABELS[stage]}
                </p>
                <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                  {formatPipelineHours(summary?.[stage])}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-label-tertiary">
                  {HOURS_PIPELINE_STAGE_DESCRIPTIONS[stage]}
                </p>
              </div>
            ))}
          </section>

          <section className="text-sm text-label-secondary">
            {detailView === "project"
              ? `${filteredProjects.length} Projekte`
              : `${filteredPersons.length} Personen`}
          </section>

          <TableSortResetBar
            visible={sortIsCustom}
            onReset={() => setSort(DEFAULT_SORT)}
          />

          {detailView === "project" ? (
            <Table
              columns={projectColumns}
              rows={filteredProjects}
              rowKey={(row) => String(row.auftrag_id ?? formatProjectLabel(row))}
              emptyText={`Keine Projekt-Stunden für ${year} gefunden.`}
              rowClassName={(row) => {
                const preInvoiced =
                  (row.stages.ohne_nachweis ?? 0) +
                  (row.stages.draft ?? 0) +
                  (row.stages.ready ?? 0) +
                  (row.stages.signed ?? 0) +
                  (row.stages.invoice_draft ?? 0);
                return preInvoiced > 0 ? "eh-hours-pipeline-row--open" : "";
              }}
            />
          ) : (
            <Table
              columns={personColumns}
              rows={filteredPersons}
              rowKey={(row) => row.person_name}
              emptyText={`Keine Personen-Stunden für ${year} gefunden.`}
              rowClassName={(row) => {
                const preInvoiced =
                  (row.stages.ohne_nachweis ?? 0) +
                  (row.stages.draft ?? 0) +
                  (row.stages.ready ?? 0) +
                  (row.stages.signed ?? 0) +
                  (row.stages.invoice_draft ?? 0);
                return preInvoiced > 0 ? "eh-hours-pipeline-row--open" : "";
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
